import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebase/config';

interface UserData {
    email: string;
    displayName?: string;
    phone: string;
}

const uploadPaymentReceipt = async (file: File, userData: UserData): Promise<string> => {
    try {
        // Verify user is authenticated
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('Unauthorized: User not authenticated');
        }

        // Format phone number (remove any non-digit characters)
        const phoneNumber = userData.phone.replace(/\D/g, '');
        
        // Validate phone number
        if (!/^[0-9]{10,11}$/.test(phoneNumber)) {
            throw new Error('Invalid phone number format');
        }

        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            throw new Error('Invalid file type. Only PDF and images are allowed.');
        }

        // Validate file size (5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            throw new Error('File size exceeds 5MB limit.');
        }

        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        
        // Create path with phone number
        const fileName = `receipts/${phoneNumber}/${timestamp}_${randomId}.${fileExtension}`;

        // Upload file using client-side Firebase Storage
        const storageRef = ref(storage, fileName);
        const uploadResult = await uploadBytes(storageRef, file, {
            customMetadata: {
                uploadedBy: userData.email,
                displayName: userData.displayName || '',
                uploadTime: new Date().toISOString(),
                phone: phoneNumber
            }
        });

        // Get download URL
        return await getDownloadURL(uploadResult.ref);
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};

const uploadSpreadImage = async (file: File): Promise<string> => {
    try {
        // Verify user is authenticated
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('Unauthorized: User not authenticated');
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            throw new Error('Invalid file type. Only images are allowed.');
        }

        // Validate file size (15MB)
        const maxSize = 15 * 1024 * 1024; // 15MB in bytes
        if (file.size > maxSize) {
            throw new Error('File size exceeds 15MB limit.');
        }

        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        
        // Create path for spread images
        const fileName = `spreads/${timestamp}_${randomId}.${fileExtension}`;

        // Upload file using client-side Firebase Storage
        const storageRef = ref(storage, fileName);
        const uploadResult = await uploadBytes(storageRef, file);

        // Get download URL
        return await getDownloadURL(uploadResult.ref);
    } catch (error) {
        console.error('Error uploading spread image:', error);
        throw error;
    }
};

export const storageService = {
    uploadPaymentReceipt,
    uploadSpreadImage
}; 